import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { UtilityService } from './utility.service';
import { ActiveHospitalService } from './active-hospital.service';
import { HospitalSettingsService } from './hospital-settings.service';
import { FeatureFlagService } from './feature-flag.service';
import { ReleaseNotesService, ReleaseNote } from './release-notes.service';
import { HospitalSiteSelectorComponent } from '../components/_shared/modals/hospital-site-selector/hospital-site-selector.component';
import { HospitalGroupEmailDialogComponent } from '../components/modules/pcv/components/vc-my-profile/modal/hospital-group-email-dialog/hospital-group-email-dialog.component';
import { ReleaseNotesPopupComponent } from '../components/_shared/support/release-notes-popup/release-notes-popup.component';

interface SitesFetchResult {
  applicable: boolean;
  sites: any[];
}

interface EmailFetchResult {
  applicable: boolean;
  settings: any;
}

interface ReleaseNotesFetchResult {
  count: number;
  releaseNotes: ReleaseNote[];
}

/**
 * Orchestrates the three hospital-user post-login pop-ups sequentially:
 *
 *   1. Hospital Site Selector (if user has >1 granted site)
 *   2. Hospital Group Email config (if hospital admin and not yet decided).
 *      "Yes" redirects to the profile Notifications settings tab, which
 *      resumes step 3 once its contact dialog closes.
 *   3. Release Notes (if unread release notes exist)
 *
 * Popups are shown one at a time, each waiting for the previous to close.
 * This is required because hospital email config depends on which hospital
 * is selected (multi-site users may switch hospitals in step 1).
 *
 * For single-site users, step 1 is skipped and email check uses the
 * user's current hospitalCode directly.
 *
 * Site selection and hospital email each self-guard via a per-login
 * sessionStorage flag so they are shown at most once per browser
 * session/login (cleared on logout, see `ApiService.logout()`).
 * Release notes re-appears if the unread count increases mid-session.
 */
@Injectable({
  providedIn: 'root'
})
export class PostLoginHospitalPopupsService {

  private siteSelectionDialogRef: MatDialogRef<HospitalSiteSelectorComponent> | null = null;
  private hospitalEmailDialogRef: MatDialogRef<HospitalGroupEmailDialogComponent> | null = null;
  private releaseNotesDialogRef: MatDialogRef<ReleaseNotesPopupComponent> | null = null;
  private checkInProgress = false;

  constructor(
    private dialog: MatDialog,
    private api: ApiService,
    private router: Router,
    private utility: UtilityService,
    private activeHospitalService: ActiveHospitalService,
    private hospitalSettingsService: HospitalSettingsService,
    private releaseNotesService: ReleaseNotesService,
    private featureFlagService: FeatureFlagService
  ) {}

  /**
   * Entry point — evaluates conditions and opens popups sequentially.
   * Safe to call repeatedly; no-op while a check or dialog is in progress.
   */
  checkAndShowPopups(userId: string): void {
    if (!userId || this.checkInProgress || this.anyLayerOpen()) {
      return;
    }

    const userType = localStorage.getItem('endUserType');
    if (userType !== 'pcv' && userType !== 'vc') {
      return;
    }

    this.checkInProgress = true;

    // Step 1: Fetch sites to determine if site selector is needed
    this.fetchSitesIfNeeded(userId).subscribe((sitesResult) => {
      this.checkInProgress = false;

      const grantedSites = (sitesResult.sites || []).filter((s: any) => s.status === 'granted');
      const needsSiteSelection = sitesResult.applicable && grantedSites.length > 1;

      if (needsSiteSelection) {
        this.openSiteSelection(sitesResult.sites, userId);
      } else {
        // Single site or no sites: skip site selector, proceed to email check
        this.proceedToEmailCheck(userId);
      }
    });
  }

  private anyLayerOpen(): boolean {
    return !!(this.siteSelectionDialogRef || this.hospitalEmailDialogRef || this.releaseNotesDialogRef);
  }

  // ─── STEP 1: Site Selection ───

  private openSiteSelection(sites: any[], userId: string): void {
    this.activeHospitalService.initialize(sites);

    this.siteSelectionDialogRef = this.dialog.open(HospitalSiteSelectorComponent, {
      width: '520px',
      panelClass: 'popup-layer-site-selection',
      hasBackdrop: true,
      disableClose: true,
      data: { sites: this.activeHospitalService.getAllSites() }
    });

    sessionStorage.setItem(this.siteSelectionKey(userId), 'true');

    this.siteSelectionDialogRef.afterClosed().subscribe((result: any) => {
      this.siteSelectionDialogRef = null;

      if (result && result.action === 'switched') {
        // Reload page for new hospital context — the next NavigationEnd
        // re-triggers checkAndShowPopups which will skip site selector
        // (sessionStorage guard) and proceed to email/release notes.
        const currentUrl = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate([currentUrl]);
        });
      } else {
        // User confirmed current site or dismissed — continue sequence
        this.proceedToEmailCheck(userId);
      }
    });
  }

  // ─── STEP 2: Hospital Group Email ───

  private proceedToEmailCheck(userId: string): void {
    // Fetch email settings using the CURRENT hospitalCode (may have been
    // updated by the site selector in step 1)
    this.fetchEmailSettingsIfNeeded().subscribe((emailResult) => {
      const hospitalCode = localStorage.getItem('hospitalCode') || '';
      const practiceName = localStorage.getItem('practiceName') || '';
      const needsEmailConfig = emailResult.applicable
        && (!emailResult.settings || !emailResult.settings.decisionMade);

      if (needsEmailConfig) {
        this.openHospitalEmail(hospitalCode, practiceName, userId);
      } else {
        this.proceedToReleaseNotes(userId);
      }
    });
  }

  private openHospitalEmail(hospitalCode: string, practiceName: string, userId: string): void {
    this.hospitalEmailDialogRef = this.dialog.open(HospitalGroupEmailDialogComponent, {
      width: '500px',
      panelClass: 'popup-layer-hospital-email',
      hasBackdrop: true,
      disableClose: true,
      // The profile Notifications settings tab only exists with Specialty Intake
      data: { hospitalCode, practiceName, redirectOnYes: this.featureFlagService.isSpecialtyIntakeEnabled() }
    });

    sessionStorage.setItem(this.hospitalEmailKey(userId), 'true');

    this.hospitalEmailDialogRef.afterClosed().subscribe((result: any) => {
      this.hospitalEmailDialogRef = null;

      // Yes: continue on the profile Notifications settings tab. Release notes
      // are deferred until that page's contact dialog closes
      // (see showReleaseNotesAfterContactSetup).
      if (result && result.action === 'configure') {
        this.router.navigate(['/pcv/my-vc-profile'], {
          queryParams: { tab: 'notifications', contacts: 'open' }
        });
        return;
      }

      if (result && result.action) {
        this.hospitalSettingsService.configureEmail({
          hospitalCode,
          action: result.action,
          email: result.email,
          phone: result.phone
        }).subscribe(
          () => {
            if (result.action === 'yes') {
              this.utility.openSnackBar('Hospital contact details configured successfully.', 'Ok');
            } else if (result.action === 'no') {
              this.utility.openSnackBar('Hospital group email declined.', 'Ok');
            }
          },
          (err: any) => {
            console.error('Error configuring hospital email:', err);
            this.utility.openSnackBar('Failed to save your selection. Please try again.', 'Ok');
          }
        );
      }

      // Proceed to release notes after email dialog closes
      this.proceedToReleaseNotes(userId);
    });
  }

  // ─── STEP 3: Release Notes ───

  /**
   * Resumes the sequence after the contact dialog opened by the Yes redirect
   * on the profile page closes, so release notes don't stack on top of it.
   */
  showReleaseNotesAfterContactSetup(): void {
    const userId = localStorage.getItem('endUserId') || '';
    if (!userId || this.anyLayerOpen()) {
      return;
    }
    this.proceedToReleaseNotes(userId);
  }

  private proceedToReleaseNotes(userId: string): void {
    this.releaseNotesService.getUnreadReleaseNotes().pipe(
      catchError(() => of({ count: 0, releaseNotes: [] as ReleaseNote[] }))
    ).subscribe((releaseNotesResult: ReleaseNotesFetchResult) => {
      if (this.shouldShowReleaseNotes(userId, releaseNotesResult.count)) {
        this.openReleaseNotes(releaseNotesResult, userId);
      }
    });
  }

  private openReleaseNotes(data: ReleaseNotesFetchResult, userId: string): void {
    this.releaseNotesDialogRef = this.dialog.open(ReleaseNotesPopupComponent, {
      width: '560px',
      maxWidth: '92vw',
      panelClass: 'popup-layer-release-notes',
      hasBackdrop: true,
      disableClose: false,
      data: { releaseNotes: data.releaseNotes }
    });

    this.setReleaseNotesPopupShownCount(userId, data.count);

    this.releaseNotesDialogRef.afterClosed().subscribe(() => {
      this.releaseNotesDialogRef = null;
    });
  }

  // ─── Data Fetchers ───

  private fetchSitesIfNeeded(userId: string): Observable<SitesFetchResult> {
    if (this.wasShownThisLogin(this.siteSelectionKey(userId))) {
      return of({ applicable: false, sites: [] });
    }
    return this.api.apiRequest('get', 'hospital-access/my-sites', {}).pipe(
      map((res: any) => ({ applicable: true, sites: res?.data || [] })),
      catchError(() => of({ applicable: false, sites: [] }))
    );
  }

  private fetchEmailSettingsIfNeeded(): Observable<EmailFetchResult> {
    const hospitalRole = localStorage.getItem('hospitalRole');
    const hospitalCode = localStorage.getItem('hospitalCode');
    const userId = localStorage.getItem('endUserId') || '';

    if (hospitalRole !== 'admin' || !hospitalCode || this.wasShownThisLogin(this.hospitalEmailKey(userId))) {
      return of({ applicable: false, settings: null });
    }

    return this.hospitalSettingsService.getSettings(hospitalCode).pipe(
      map((res: any) => ({ applicable: true, settings: res?.data?.settings || null })),
      catchError(() => of({ applicable: false, settings: null }))
    );
  }

  // ─── Session Guards ───

  private siteSelectionKey(userId: string): string {
    return `siteSelectionPopupShownForLogin_${userId}`;
  }

  private hospitalEmailKey(userId: string): string {
    return `hospitalEmailPopupShownForLogin_${userId}`;
  }

  private wasShownThisLogin(key: string): boolean {
    return sessionStorage.getItem(key) === 'true';
  }

  private releaseNotesPopupCountStorageKey(userId: string): string {
    return `releaseNotesPopupShownCount_${userId}`;
  }

  private getReleaseNotesPopupShownCount(userId: string): number | null {
    const raw = sessionStorage.getItem(this.releaseNotesPopupCountStorageKey(userId));
    return raw !== null ? parseInt(raw, 10) : null;
  }

  private setReleaseNotesPopupShownCount(userId: string, count: number): void {
    sessionStorage.setItem(this.releaseNotesPopupCountStorageKey(userId), String(count));
  }

  private shouldShowReleaseNotes(userId: string, count: number): boolean {
    if (!count) {
      return false;
    }
    const lastShown = this.getReleaseNotesPopupShownCount(userId);
    if (lastShown !== null && count <= lastShown) {
      return false;
    }
    return true;
  }
}
